import * as Yup from 'yup';
import { FC, useState, useMemo } from 'react';
import { Box, withStyles, Card, ElementContainer, TextField } from 'vopay';
import { useTranslation } from 'react-i18next';
import { Title, Button } from 'UI';
import { useDataFetchIndicator } from '@/hooks';
import { useToast } from '@/providers/Toast.provider';
import usePostCreateUser from '@/hooks/useApi/usePostCreateAccountUser';
import styles from './AddUserDialog.module.scss';

const cn = withStyles(styles);

type AddUserDialogProps = {
  onClose: () => void;
  onSuccess: () => void;
  existingEmails: string[];
  accountToken: string;
};

const AddUserDialog: FC<AddUserDialogProps> = ({ onClose, onSuccess, existingEmails, accountToken }) => {
  const toast = useToast();
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.UserManagement.AddUser' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global.Buttons' });
  const [, createUserAsync, ,] = usePostCreateUser({ bearer: accountToken });
  const [, setLoading] = useDataFetchIndicator();
  const [emailAddress, setEmailAddress] = useState('');

  const { isEmailValid, isDuplicateEmail, emailInlineError } = useMemo(() => {
    const isEmailValid = Yup.string().email().required().isValidSync(emailAddress);
    const isDuplicateEmail = existingEmails.some((email) => email.toLowerCase() === emailAddress.toLowerCase());
    const emailInlineError = isDuplicateEmail
      ? t('duplicateEmail', { defaultValue: 'This email is already used. Please use a different email.' })
      : undefined;
    return { isEmailValid, isDuplicateEmail, emailInlineError };
  }, [emailAddress, existingEmails, t]);

  const addUser = async () => {
    try {
      setLoading(true);
      onClose();
      await createUserAsync({
        EmailAddress: emailAddress,
        SendEmail: true,
      });
      setEmailAddress('');
      onSuccess();
    } catch (err) {
      console.error(err);
      toast.neutral(t('failMessage', { defaultValue: 'Unable to add a user.' }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className={cn('card')}>
      <Box flx="col" gapSize="24px">
        <Title level="4" className={cn('title')}>
          {t('title', { defaultValue: 'Add a New User?' })}
        </Title>

        <ElementContainer
          label={t('emailAddress', { defaultValue: 'Email Address' })}
          isBold={false}
          padding="0 0 8px 0"
          required
          input={
            <TextField
              name="EmailAddress"
              value={emailAddress}
              variant="outlineShadow"
              error={emailInlineError}
              errorFitContent
              onChange={(e) => {
                setEmailAddress(e.target.value);
              }}
            />
          }
        />

        <div className={cn('buttonContainer')}>
          <Button
            size="small"
            onClick={() => {
              setEmailAddress('');
              onClose();
            }}
          >
            {tGlobal('cancel')}
          </Button>
          <Button size="small" onClick={addUser} colorScheme="primary" disabled={!isEmailValid || isDuplicateEmail}>
            {tGlobal('save')}
          </Button>
        </div>
      </Box>
    </Card>
  );
};

export default AddUserDialog;
